import React, { useMemo, useState } from 'react';
import { Badge, Button, Form } from 'react-bootstrap';
import type { ExportFieldItem, SelectionMode } from './api';
import { getFieldTypeLabel } from './fieldTypeLabels';

interface Props {
  fields: ExportFieldItem[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  selectionMode: SelectionMode;
  onSelectionModeChange: (mode: SelectionMode) => void;
  loading?: boolean;
  disabled?: boolean;
}

export interface ExportFieldGroup {
  key: string;
  label: string;
  fieldType: string;
  branchContext: string;
  fields: ExportFieldItem[];
}

export function uniqueExportFields(fields: ExportFieldItem[]): ExportFieldItem[] {
  const seen = new Set<number>();
  const unique: ExportFieldItem[] = [];
  fields.forEach((field) => {
    if (seen.has(field.field_id)) return;
    seen.add(field.field_id);
    unique.push(field);
  });
  return unique;
}

export function groupExportFields(fields: ExportFieldItem[]): ExportFieldGroup[] {
  const map = new Map<string, ExportFieldItem[]>();
  uniqueExportFields(fields).forEach((field) => {
    const key = `${field.workflow_id}::${field.group_name || ''}::${field.label}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(field);
  });
  return Array.from(map.entries()).map(([key, groupFields]) => {
    const first = groupFields[0];
    const contexts = Array.from(
      new Set(groupFields.map((field) => field.branch_context).filter(Boolean)),
    );
    return {
      key,
      label: first.label,
      fieldType: first.field_type,
      branchContext:
        contexts.length === 1 ? contexts[0] : contexts.length > 1 ? `${contexts.length} ramos` : '',
      fields: groupFields,
    };
  });
}

const ExportFieldPicker: React.FC<Props> = ({
  fields,
  selectedIds,
  onChange,
  selectionMode,
  onSelectionModeChange,
  loading,
  disabled,
}) => {
  const [query, setQuery] = useState('');
  const uniqueFields = useMemo(() => uniqueExportFields(fields), [fields]);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return uniqueFields;
    return uniqueFields.filter((field) => {
      const haystack = [field.label, field.header, field.name, field.workflow_name, field.group_name, field.branch_context]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [uniqueFields, query]);

  const grouped = useMemo(() => {
    const map = new Map<string, Map<string, ExportFieldGroup[]>>();
    groupExportFields(filtered).forEach((group) => {
      const first = group.fields[0];
      const workflow = first.workflow_name || 'Sem fluxo';
      const section = first.group_name || 'Geral';
      if (!map.has(workflow)) map.set(workflow, new Map());
      const bySection = map.get(workflow)!;
      if (!bySection.has(section)) bySection.set(section, []);
      bySection.get(section)!.push(group);
    });
    return map;
  }, [filtered]);

  const toggleGroup = (group: ExportFieldGroup, checked: boolean) => {
    const groupIds = new Set(group.fields.map((field) => field.field_id));
    if (checked) {
      const next = new Set(selectedIds);
      groupIds.forEach((id) => next.add(id));
      onChange(Array.from(next));
      return;
    }
    onChange(selectedIds.filter((id) => !groupIds.has(id)));
  };

  const visibleIds = filtered.map((field) => field.field_id);
  const allGroups = useMemo(() => groupExportFields(uniqueFields), [uniqueFields]);
  const selectedGroupCount = allGroups.filter((group) =>
    group.fields.every((field) => selectedSet.has(field.field_id)),
  ).length;
  const totalGroupCount = allGroups.length;
  const selectVisible = () => {
    const next = new Set(selectedIds);
    visibleIds.forEach((id) => next.add(id));
    onChange(Array.from(next));
  };
  const clearVisible = () => {
    const visible = new Set(visibleIds);
    onChange(selectedIds.filter((id) => !visible.has(id)));
  };

  return (
    <div className="export-field-picker">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div className="text-muted small">
          {selectionMode === 'automatic'
            ? 'Modo automático: o backend escolhe as colunas aplicáveis no recorte filtrado.'
            : 'Modo manual: as colunas escolhidas são mantidas mesmo vazias.'}
        </div>
        <Badge bg="secondary">
          {selectedGroupCount}/{totalGroupCount} selecionados
        </Badge>
      </div>

      <div className="d-flex flex-wrap gap-3 mb-3">
        <Form.Check
          type="radio"
          id="export-mode-auto"
          name="export-selection-mode"
          label="Seleção automática (recomendado)"
          checked={selectionMode === 'automatic'}
          disabled={disabled}
          onChange={() => onSelectionModeChange('automatic')}
        />
        <Form.Check
          type="radio"
          id="export-mode-manual"
          name="export-selection-mode"
          label="Seleção manual"
          checked={selectionMode === 'manual'}
          disabled={disabled}
          onChange={() => onSelectionModeChange('manual')}
        />
      </div>

      <Form.Control
        className="mb-3"
        size="sm"
        type="search"
        placeholder="Buscar campo, fluxo ou agrupamento..."
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        disabled={disabled || loading}
      />

      <div className="d-flex gap-2 mb-3">
        <Button size="sm" variant="outline-secondary" onClick={selectVisible} disabled={disabled || filtered.length === 0}>
          Selecionar visíveis
        </Button>
        <Button size="sm" variant="outline-secondary" onClick={clearVisible} disabled={disabled || filtered.length === 0}>
          Limpar visíveis
        </Button>
      </div>

      {loading ? (
        <p className="text-muted mb-0">Carregando campos...</p>
      ) : fields.length === 0 ? (
        <p className="text-muted mb-0">Nenhum campo disponível para o recorte atual. Aplique um filtro ou escolha um fluxo.</p>
      ) : filtered.length === 0 ? (
        <p className="text-muted mb-0">Nenhum campo encontrado para esta busca.</p>
      ) : (
        <div className="export-field-list">
          {Array.from(grouped.entries()).map(([workflow, sections]) => (
            <section key={workflow} className="mb-3">
              <h6 className="export-field-workflow">{workflow}</h6>
              {Array.from(sections.entries()).map(([sectionName, sectionGroups]) => (
                <div key={`${workflow}-${sectionName}`} className="mb-2">
                  <div className="small text-muted mb-1">{sectionName}</div>
                  {sectionGroups.map((group) => {
                    const checked = group.fields.every((field) => selectedSet.has(field.field_id));
                    return (
                      <Form.Check
                        key={group.key}
                        type="checkbox"
                        id={`export-field-group-${group.key}`}
                        className="mb-2"
                        disabled={disabled || selectionMode === 'automatic'}
                        checked={checked}
                        onChange={(event) => toggleGroup(group, event.target.checked)}
                        label={
                          <span>
                            <strong>{group.label}</strong>
                            {group.branchContext ? (
                              <span className="text-muted"> — {group.branchContext}</span>
                            ) : null}
                            <span className="export-field-type"> {getFieldTypeLabel(group.fieldType)}</span>
                          </span>
                        }
                      />
                    );
                  })}
                </div>
              ))}
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExportFieldPicker;
